import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAgent,
  deleteAgent,
  getAgent,
  getAgentsList,
  getClientAgentsList,
  updateAgent,
} from '../../api/agentsApi';
import { Agent, ICreateAgentParams } from '../../api/agentsApi/interfaces';
import { IUseAgents } from './interfaces';
import { getAgents } from '../../api/agentsApi';
import { AxiosError } from 'axios';

export const useGetAgent = (id: number) => {
  return useQuery<Agent>({
    queryFn: () => getAgent(id),
    queryKey: ['agent', id],
    enabled: !!id,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export const useGetAgents = ({
  page,
  pageSize,
  sortField,
  search,
}: IUseAgents) => {
  return useQuery({
    queryKey: ['agents', page, pageSize, sortField, search],
    queryFn: () => {
      return getAgents(page + 1, pageSize, sortField, search || undefined);
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetAgentsList = (enabled: boolean = true) => {
  return useQuery<Agent[]>({
    queryFn: () => getAgentsList(),
    queryKey: ['agents-list'],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled,
  });
};

export const useGetClientAgentsList = (
  clientId: number | undefined,
  enabled: boolean = true
) => {
  return useQuery<Agent[]>({
    queryFn: () => getClientAgentsList(clientId || 0),
    queryKey: ['client-agents-list', clientId],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: enabled && clientId !== undefined,
  });
};

export const useCreateAgentMutation = () => {
  return useMutation<number, AxiosError, ICreateAgentParams>({
    mutationFn: createAgent,
  });
};

export const useUpdateAgentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    number,
    AxiosError,
    { agentId: number; agentData: ICreateAgentParams }
  >({
    mutationFn: async ({ agentId, agentData }) =>
      updateAgent(agentId, agentData),
    onSuccess: (agentId: number) => {
      queryClient.invalidateQueries({
        queryKey: ['agent', agentId],
      });
    },
  });
};

export const useDeleteAgent = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, number>({
    mutationFn: deleteAgent,
    onSuccess: (_, agentId) => {
      queryClient.removeQueries(['agent', agentId]);
    },
  });
};
